import { useState, useEffect } from 'react';
import { Users, UserPlus, Shield, Activity, BookOpen } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { DataCard } from '../components/common/DataCard';
import { StatsCard } from '../components/common/StatsCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useApp } from '../contexts/AppContext';
import api from '../services/api';
import { toast } from 'sonner';

const roles = ['admin', 'teacher', 'finance_admin'];
const classes = ['9', '10', '11', '12'];
const sections = ['A', 'B', 'C', 'D'];

function getInitials(name) {
  return name?.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2) || 'U';
}

export function AdministrationPage() {
  const { currentUser } = useApp();
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Add user modal
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [userForm, setUserForm] = useState({
    name: '', email: '', password: '', role: 'teacher', phone: '', assignedClasses: [],
  });
  const [isSaving, setIsSaving] = useState(false);
  const [selectedClassForAssign, setSelectedClassForAssign] = useState('');
  const [selectedSectionForAssign, setSelectedSectionForAssign] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [usersData, logsData] = await Promise.all([
        api.listUsers(),
        api.getAuditLogs(null, null, 50).catch(() => []),
      ]);
      setUsers(usersData);
      setAuditLogs(logsData);
    } catch (error) {
      console.error('Failed to load data', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (!userForm.name || !userForm.email || !userForm.password || !userForm.role) {
      toast.error('Please fill Name, Email, Password, and Role');
      return;
    }
    setIsSaving(true);
    try {
      await api.createUser({
        name: userForm.name,
        email: userForm.email,
        password: userForm.password,
        role: userForm.role,
        assignedClasses: userForm.role === 'teacher' ? userForm.assignedClasses : [],
      });
      toast.success(`${userForm.name} added as ${userForm.role}`);
      setIsAddUserOpen(false);
      setUserForm({ name: '', email: '', password: '', role: 'teacher', phone: '', assignedClasses: [] });
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to add user');
    } finally {
      setIsSaving(false);
    }
  };

  const addAssignedClass = () => {
    if (!selectedClassForAssign || !selectedSectionForAssign) return;
    const cls = `${selectedClassForAssign}-${selectedSectionForAssign}`;
    if (!userForm.assignedClasses.includes(cls)) {
      setUserForm({ ...userForm, assignedClasses: [...userForm.assignedClasses, cls] });
    }
    setSelectedClassForAssign('');
    setSelectedSectionForAssign('');
  };

  const removeAssignedClass = (cls) => {
    setUserForm({ ...userForm, assignedClasses: userForm.assignedClasses.filter(c => c !== cls) });
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin': return <Badge className="bg-brand-deep-blue/10 text-brand-deep-blue">Admin</Badge>;
      case 'teacher': return <Badge className="bg-emerald-500/10 text-emerald-600">Teacher</Badge>;
      case 'parent': return <Badge className="bg-amber-500/10 text-amber-600">Parent</Badge>;
      case 'student': return <Badge className="bg-blue-500/10 text-blue-600">Student</Badge>;
      case 'finance_admin': return <Badge className="bg-purple-500/10 text-purple-600">Finance</Badge>;
      default: return <Badge variant="outline">{role}</Badge>;
    }
  };

  // Live stats
  const adminCount = users.filter(u => u.role === 'admin').length;
  const teacherCount = users.filter(u => u.role === 'teacher').length;
  const parentCount = users.filter(u => u.role === 'parent').length;
  const totalUsers = users.length;

  return (
    <div data-testid="administration-page" className="space-y-6 animate-fade-in">
      <PageHeader title="Administration" description="Manage users, roles, and system operations.">
        <Button
          className="bg-brand-deep-blue hover:bg-brand-deep-blue/90"
          onClick={() => setIsAddUserOpen(true)}
          data-testid="add-user-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </PageHeader>

      {/* Live Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard title="Total Users" value={isLoading ? '...' : totalUsers} icon={Users} delay={0} />
        <StatsCard title="Admins" value={isLoading ? '...' : adminCount} icon={Shield} delay={0.05} />
        <StatsCard title="Teachers" value={isLoading ? '...' : teacherCount} icon={BookOpen} delay={0.1} />
        <StatsCard title="Parents" value={isLoading ? '...' : parentCount} icon={Users} delay={0.15} />
      </div>

      <Tabs defaultValue="users" className="space-y-4">
        <TabsList data-testid="admin-tabs">
          <TabsTrigger value="users">Users ({users.length})</TabsTrigger>
          <TabsTrigger value="audit-logs">Audit Logs ({auditLogs.length})</TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users">
          <DataCard>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-brand-light-blue border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 dark:bg-slate-800/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold">User</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Role</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Joined</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                      {users.map((user) => (
                        <tr key={user.userId} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors" data-testid={`user-row-${user.userId}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <Avatar className="w-8 h-8">
                                <AvatarFallback className="bg-brand-light-blue/10 text-brand-deep-blue text-xs">{getInitials(user.name)}</AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium text-sm">{user.name}</p>
                                <p className="text-xs text-muted-foreground">{user.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">{getRoleBadge(user.role)}</td>
                          <td className="px-4 py-3">
                            <Badge className={user.status === 'active' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-slate-200 text-slate-600'}>
                              {user.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">
                            {new Date(user.createdAt).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </DataCard>
        </TabsContent>

        {/* Audit Logs Tab */}
        <TabsContent value="audit-logs">
          <DataCard>
            {auditLogs.length > 0 ? (
              <div className="space-y-3">
                {auditLogs.map((log, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-800" data-testid={`audit-log-${idx}`}>
                    <Activity className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm">
                        <span className="font-medium">{log.performerName || log.performedBy}</span>
                        {' '}<span className="text-muted-foreground">{log.action.toLowerCase().replace(/_/g, ' ')}</span>
                        {' '}on <span className="font-medium">{log.entity}</span>
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">{new Date(log.timestamp).toLocaleString()}</p>
                    </div>
                    <Badge variant="outline" className="capitalize text-xs">{log.role}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Activity className="w-12 h-12 mx-auto mb-3" />
                <p>No audit logs yet</p>
              </div>
            )}
          </DataCard>
        </TabsContent>
      </Tabs>

      {/* Add User Modal */}
      <Dialog open={isAddUserOpen} onOpenChange={setIsAddUserOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5" /> Add New User</DialogTitle>
            <DialogDescription>Create a new user for your school. The schoolId is assigned automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Name *</Label>
              <Input value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} placeholder="Full name" data-testid="new-user-name" />
            </div>
            <div>
              <Label>Role *</Label>
              <Select value={userForm.role} onValueChange={(v) => setUserForm({ ...userForm, role: v })}>
                <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {roles.map(r => <SelectItem key={r} value={r} className="capitalize">{r.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} placeholder="user@email.com" data-testid="new-user-email" />
            </div>
            <div>
              <Label>Password *</Label>
              <Input type="password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} placeholder="Set password" data-testid="new-user-password" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={userForm.phone} onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })} placeholder="+91 XXXXXXXXXX" data-testid="new-user-phone" />
            </div>

            {/* Assigned Classes for Teacher */}
            {userForm.role === 'teacher' && (
              <div>
                <Label>Assigned Classes</Label>
                <div className="flex gap-2 mt-1.5">
                  <Select value={selectedClassForAssign} onValueChange={setSelectedClassForAssign}>
                    <SelectTrigger className="w-[100px]"><SelectValue placeholder="Class" /></SelectTrigger>
                    <SelectContent>
                      {classes.map(c => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={selectedSectionForAssign} onValueChange={setSelectedSectionForAssign}>
                    <SelectTrigger className="w-[100px]"><SelectValue placeholder="Section" /></SelectTrigger>
                    <SelectContent>
                      {sections.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={addAssignedClass} data-testid="assign-class-btn">Add</Button>
                </div>
                {userForm.assignedClasses.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {userForm.assignedClasses.map(cls => (
                      <Badge key={cls} variant="secondary" className="cursor-pointer hover:bg-red-100" onClick={() => removeAssignedClass(cls)}>
                        {cls} ×
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsAddUserOpen(false)}>Cancel</Button>
            <Button onClick={handleAddUser} disabled={isSaving} className="bg-brand-deep-blue hover:bg-brand-deep-blue/90" data-testid="save-new-user-btn">
              {isSaving ? 'Adding...' : 'Add User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
